/**
 * Module String 간 이동 — 낙관적 in-place UI + flattened patch + rollback
 */
import {
  isModuleMapFeatureWithSeq,
  readStringDisplayNo,
  readStringSeq,
  writeStringIdentityOnFeature
} from './panelMapFeatureProps.js';
import {
  moveModuleInPanelTreeInPlace,
  updatePanelTreeNodeEntityDisplayInPlace
} from './panelTreeLocalRegistry.js';
import { getPanelTreeModuleChildren } from './panelTreeModuleChildrenIndex.js';
import { insertFlatModuleInSeqOrder } from './panelModuleSeqOrder.js';

function lookupModuleInfo(flattenedSiteDesign, moduleSeq) {
  if (!flattenedSiteDesign?.byModule || moduleSeq == null) {
    return null;
  }
  return (
    flattenedSiteDesign.byModule.get(moduleSeq) ??
    flattenedSiteDesign.byModule.get(Number(moduleSeq)) ??
    flattenedSiteDesign.byModule.get(String(moduleSeq)) ??
    null
  );
}

function applyModuleDisplayUpdate(moduleRecord, displayUpdate) {
  if (!moduleRecord || !displayUpdate) {
    return;
  }

  const displayNo =
    displayUpdate.displayNo ?? displayUpdate.moduleId ?? displayUpdate.solarModuleId;
  if (displayNo != null && displayNo !== '') {
    const normalizedDisplayNo = String(displayNo);
    moduleRecord.displayNo = normalizedDisplayNo;
    moduleRecord.moduleId = normalizedDisplayNo;
    moduleRecord.solarModuleId = normalizedDisplayNo;
  }
  if (displayUpdate.name != null && displayUpdate.name !== '') {
    moduleRecord.name = displayUpdate.name;
  }
}

function readModuleSeq(moduleRecord) {
  if (!moduleRecord) {
    return null;
  }
  if (moduleRecord.solarModuleSeq ?? moduleRecord.seq) {
    return moduleRecord.solarModuleSeq ?? moduleRecord.seq;
  }
  const rawId = String(moduleRecord.id ?? '');
  return rawId.startsWith('module_') ? rawId.slice('module_'.length) : rawId;
}

function findSolarStringRecord(solarStrings, stringSeq) {
  if (!Array.isArray(solarStrings) || stringSeq == null) {
    return null;
  }
  const key = String(stringSeq);
  return (
    solarStrings.find(s => String(s.id) === key || String(s.stringId) === key) ?? null
  );
}

function detachModuleFromFlattenedStringBuckets(flattenedSiteDesign, moduleSeq) {
  const key = String(moduleSeq);
  const detachFromStringInfo = stringInfo => {
    if (!stringInfo) {
      return;
    }
    if (stringInfo.solarModules) {
      const idx = stringInfo.solarModules.findIndex(module => String(module.id) === key);
      if (idx > -1) {
        stringInfo.solarModules.splice(idx, 1);
      }
    }
    if (stringInfo.children) {
      const idx = stringInfo.children.findIndex(
        child => child?.level === 'module' && String(child.id) === key
      );
      if (idx > -1) {
        stringInfo.children.splice(idx, 1);
      }
    }
  };

  for (const stringInfo of flattenedSiteDesign.byString?.values() || []) {
    detachFromStringInfo(stringInfo);
  }
  for (const arrayInfo of flattenedSiteDesign.byArray?.values() || []) {
    arrayInfo.solarStrings?.forEach(detachFromStringInfo);
  }
}

function attachModuleToFlattenedStringBuckets(flattenedSiteDesign, targetStringSeq, moduleInfo) {
  const stringInfo =
    flattenedSiteDesign.byString?.get(targetStringSeq) ??
    flattenedSiteDesign.byString?.get(Number(targetStringSeq)) ??
    flattenedSiteDesign.byString?.get(String(targetStringSeq));
  if (!stringInfo || !moduleInfo) {
    return;
  }
  if (!stringInfo.solarModules) {
    stringInfo.solarModules = [];
  }
  if (!stringInfo.solarModules.some(module => String(module.id) === String(moduleInfo.id))) {
    insertFlatModuleInSeqOrder(stringInfo.solarModules, moduleInfo);
  }
  if (!stringInfo.children) {
    stringInfo.children = [];
  }
  if (!stringInfo.children.some(child => String(child.id) === String(moduleInfo.id))) {
    insertFlatModuleInSeqOrder(stringInfo.children, moduleInfo);
  }
}

function patchFlattenedModuleParentInPlace(
  flattenedSiteDesign,
  solarStrings,
  moduleSeq,
  targetStringSeq,
  targetStringDisplayId,
  displayUpdate = null
) {
  const moduleInfo = lookupModuleInfo(flattenedSiteDesign, moduleSeq);
  if (!moduleInfo) {
    return false;
  }

  const resolvedSeq = moduleInfo.id ?? moduleSeq;
  const targetParentId = Number(targetStringSeq) || targetStringSeq;

  moduleInfo.parentId = targetParentId;
  if (targetStringDisplayId != null) {
    moduleInfo.stringId = String(targetStringDisplayId);
  }
  applyModuleDisplayUpdate(moduleInfo, displayUpdate);

  const flatRow = flattenedSiteDesign.flattened?.find(
    entry => entry.level === 'module' && String(entry.id) === String(resolvedSeq)
  );
  if (flatRow) {
    flatRow.parentId = targetParentId;
    if (targetStringDisplayId != null) {
      flatRow.stringId = String(targetStringDisplayId);
    }
    applyModuleDisplayUpdate(flatRow, displayUpdate);
  }

  detachModuleFromFlattenedStringBuckets(flattenedSiteDesign, resolvedSeq);
  attachModuleToFlattenedStringBuckets(flattenedSiteDesign, targetParentId, moduleInfo);

  let movedModule = null;
  for (const solarString of solarStrings || []) {
    const idx = solarString.solarModules?.findIndex(m => String(m.id) === String(resolvedSeq));
    if (idx > -1) {
      [movedModule] = solarString.solarModules.splice(idx, 1);
      break;
    }
  }

  if (movedModule) {
    movedModule.parentId = targetParentId;
    applyModuleDisplayUpdate(movedModule, displayUpdate);
    const targetSolar = findSolarStringRecord(solarStrings, targetParentId);
    if (targetSolar) {
      if (!targetSolar.solarModules) {
        targetSolar.solarModules = [];
      }
      insertFlatModuleInSeqOrder(targetSolar.solarModules, movedModule);
    }
  }

  return true;
}

function snapshotMapModuleFeatures(vectorSource, moduleSeq) {
  if (!vectorSource?.getFeatures) {
    return [];
  }
  return vectorSource
    .getFeatures()
    .filter(feature => isModuleMapFeatureWithSeq(feature, moduleSeq))
    .map(feature => ({
      stringSeq: readStringSeq(feature),
      stringDisplayNo: readStringDisplayNo(feature),
      moduleDisplayNo: feature.get('solarModuleId') ?? feature.get('moduleId') ?? null,
      parentStringName: feature.get('parentStringName') ?? null
    }));
}

function applyMapModuleMove(
  vectorSource,
  moduleSeq,
  targetStringItem,
  targetStringSeq,
  targetStringDisplayId,
  displayUpdate = null
) {
  if (!vectorSource?.getFeatures || !targetStringItem) {
    return;
  }
  vectorSource.getFeatures().forEach(feature => {
    if ((feature.get('featureType') || '').toLowerCase() !== 'module') {
      return;
    }
    if (!isModuleMapFeatureWithSeq(feature, moduleSeq)) {
      return;
    }
    writeStringIdentityOnFeature(feature, {
      seq: targetStringSeq,
      displayNo: targetStringDisplayId ?? targetStringSeq
    });
    const moduleDisplayNo =
      displayUpdate?.displayNo ?? displayUpdate?.moduleId ?? displayUpdate?.solarModuleId;
    if (moduleDisplayNo != null && moduleDisplayNo !== '') {
      feature.set('solarModuleId', String(moduleDisplayNo));
      feature.set('moduleId', String(moduleDisplayNo));
    }
    if (displayUpdate?.name) {
      feature.set('name', displayUpdate.name);
    }
    feature.set('parentStringName', targetStringItem.name);
  });
}

function restoreMapModuleFeatures(vectorSource, moduleSeq, mapSnapshots) {
  if (!vectorSource?.getFeatures || !mapSnapshots?.length) {
    return;
  }
  const features = vectorSource
    .getFeatures()
    .filter(feature => isModuleMapFeatureWithSeq(feature, moduleSeq));
  features.forEach((feature, index) => {
    const saved = mapSnapshots[index] ?? mapSnapshots[0];
    if (!saved) {
      return;
    }
    writeStringIdentityOnFeature(feature, {
      seq: saved.stringSeq,
      displayNo: saved.stringDisplayNo
    });
    if (saved.moduleDisplayNo != null) {
      feature.set('solarModuleId', saved.moduleDisplayNo);
      feature.set('moduleId', saved.moduleDisplayNo);
    }
    if (saved.parentStringName != null) {
      feature.set('parentStringName', saved.parentStringName);
    }
  });
}

/**
 * @param {object} ctx
 * @returns {object|null}
 */
export function createModuleMoveSnapshot(ctx) {
  const {
    flattenedSiteDesign,
    solarStrings,
    vectorSource,
    sourceModuleItem,
    targetStringItem,
    moduleSeq,
    targetStringSeq,
    targetStringDisplayId,
    numberingAssignment = null,
    sourceStringNodeId = null,
    snapshotStringModulesState
  } = ctx;

  if (
    !flattenedSiteDesign ||
    !sourceModuleItem ||
    !targetStringItem ||
    moduleSeq == null ||
    targetStringSeq == null
  ) {
    return null;
  }

  const moduleInfo = lookupModuleInfo(flattenedSiteDesign, moduleSeq);
  if (!moduleInfo) {
    return null;
  }

  const sourceStringSeq = moduleInfo.parentId;
  const flatRow = flattenedSiteDesign.flattened?.find(
    entry => entry.level === 'module' && String(entry.id) === String(moduleInfo.id ?? moduleSeq)
  );
  const originalModuleDisplay = {
    displayNo: moduleInfo.displayNo ?? moduleInfo.moduleId ?? moduleInfo.solarModuleId ?? null,
    moduleId: moduleInfo.moduleId ?? null,
    solarModuleId: moduleInfo.solarModuleId ?? null,
    name: moduleInfo.name ?? null
  };

  const sourceSolar = findSolarStringRecord(solarStrings, sourceStringSeq);
  const targetSolar = findSolarStringRecord(solarStrings, targetStringSeq);

  return {
    moduleSeq,
    sourceModuleItem,
    targetStringItem,
    sourceStringSeq,
    targetStringSeq,
    targetStringDisplayId,
    sourceStringNodeId,
    targetStringNodeId: targetStringItem.id,
    flattened: {
      moduleParentId: moduleInfo.parentId,
      moduleStringId: moduleInfo.stringId ?? null,
      moduleDisplay: originalModuleDisplay,
      flatRowParentId: flatRow?.parentId ?? null,
      flatRowStringId: flatRow?.stringId ?? null
    },
    sourceStringTree:
      sourceStringNodeId && snapshotStringModulesState
        ? snapshotStringModulesState(sourceStringNodeId)
        : null,
    targetStringTree: snapshotStringModulesState
      ? snapshotStringModulesState(targetStringItem.id)
      : null,
    numberingAssignment,
    solarStrings: {
      sourceId: sourceSolar?.id ?? null,
      targetId: targetSolar?.id ?? null,
      sourceModules: sourceSolar?.solarModules ? [...sourceSolar.solarModules] : null,
      targetModules: targetSolar?.solarModules ? [...targetSolar.solarModules] : null
    },
    mapFeatures: snapshotMapModuleFeatures(vectorSource, moduleSeq)
  };
}

/**
 * @returns {boolean} tree in-place 이동 성공 여부
 */
export function applyOptimisticModuleMove(ctx, snapshot) {
  const { flattenedSiteDesign, solarStrings, vectorSource } = ctx;
  if (!snapshot) {
    return false;
  }

  const treeMoved = moveModuleInPanelTreeInPlace(
    snapshot.sourceModuleItem,
    snapshot.targetStringItem
  );
  if (!treeMoved) {
    return false;
  }

  patchFlattenedModuleParentInPlace(
    flattenedSiteDesign,
    solarStrings,
    snapshot.moduleSeq,
    snapshot.targetStringSeq,
    snapshot.targetStringDisplayId,
    snapshot.numberingAssignment
  );

  applyMapModuleMove(
    vectorSource,
    snapshot.moduleSeq,
    snapshot.targetStringItem,
    snapshot.targetStringSeq,
    snapshot.targetStringDisplayId,
    snapshot.numberingAssignment
  );

  applyModuleDisplayUpdate(snapshot.sourceModuleItem, snapshot.numberingAssignment);
  if (snapshot.numberingAssignment) {
    updatePanelTreeNodeEntityDisplayInPlace(snapshot.sourceModuleItem.id, {
      entityType: 'module',
      newName: snapshot.numberingAssignment.name,
      displayId: snapshot.numberingAssignment.displayNo
    });
  }
  getPanelTreeModuleChildren(snapshot.targetStringNodeId).forEach(moduleNode => {
    if (String(readModuleSeq(moduleNode)) === String(snapshot.moduleSeq)) {
      applyModuleDisplayUpdate(moduleNode, snapshot.numberingAssignment);
    }
  });

  return true;
}

export function rollbackModuleMove(ctx, snapshot) {
  const {
    flattenedSiteDesign,
    solarStrings,
    vectorSource,
    restoreStringModulesState
  } = ctx;

  if (!snapshot || !flattenedSiteDesign) {
    return;
  }

  if (snapshot.sourceStringTree && restoreStringModulesState) {
    restoreStringModulesState(snapshot.sourceStringTree);
  }
  if (snapshot.targetStringTree && restoreStringModulesState) {
    restoreStringModulesState(snapshot.targetStringTree);
  }

  const moduleInfo = lookupModuleInfo(flattenedSiteDesign, snapshot.moduleSeq);
  if (moduleInfo) {
    moduleInfo.parentId = snapshot.flattened.moduleParentId;
    moduleInfo.stringId = snapshot.flattened.moduleStringId;
    applyModuleDisplayUpdate(moduleInfo, snapshot.flattened.moduleDisplay);

    const flatRow = flattenedSiteDesign.flattened?.find(
      entry =>
        entry.level === 'module' &&
        String(entry.id) === String(moduleInfo.id ?? snapshot.moduleSeq)
    );
    if (flatRow) {
      flatRow.parentId = snapshot.flattened.flatRowParentId;
      flatRow.stringId = snapshot.flattened.flatRowStringId;
      applyModuleDisplayUpdate(flatRow, snapshot.flattened.moduleDisplay);
    }

    detachModuleFromFlattenedStringBuckets(
      flattenedSiteDesign,
      moduleInfo.id ?? snapshot.moduleSeq
    );
    if (snapshot.flattened.moduleParentId != null) {
      attachModuleToFlattenedStringBuckets(
        flattenedSiteDesign,
        snapshot.flattened.moduleParentId,
        moduleInfo
      );
    }
  }

  const sourceSolar = findSolarStringRecord(solarStrings, snapshot.solarStrings.sourceId);
  const targetSolar = findSolarStringRecord(solarStrings, snapshot.solarStrings.targetId);
  if (sourceSolar && snapshot.solarStrings.sourceModules) {
    sourceSolar.solarModules = [...snapshot.solarStrings.sourceModules];
  }
  if (targetSolar && snapshot.solarStrings.targetModules) {
    targetSolar.solarModules = [...snapshot.solarStrings.targetModules];
  }

  restoreMapModuleFeatures(vectorSource, snapshot.moduleSeq, snapshot.mapFeatures);
}
